import { Tooltip } from '@mui/material';
import { useAppSelector } from 'store/store';
import { classNames } from 'shared/utils/classNames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const MenuItem = ({
  item,
  collapsed,
  t,
  groups,
  context
}: {
  item: any;
  collapsed?: boolean;
  t: any;
  groups: any;
  context: any;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // 1. FILTRO FIGLI: Nasconde tassativamente i figli che non appartengono al contesto attivo
  const filteredChildren = useMemo(() => {
    return (item.children || []).filter((child: any) => {
      const roleMatch = !child.roles || child.roles.length === 0 || child.roles.includes(groups[0]);

      // Controllo Contesto Rigido per i figli
      const hasContexts = child.contexts && child.contexts.length > 0;
      const contextMatch = hasContexts ? child.contexts.includes(context) : true;

      return roleMatch && contextMatch;
    });
  }, [item.children, groups, context]);

  const hasChildren = filteredChildren.length > 0;
  const label = item?.translate === false ? item.name : t(`menu.${item.name}`);

  if (hasChildren) {
    return (
      <div className="w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            isOpen ? 'bg-[#e2e6ea]/60 text-[#1c1f23]' : 'text-[#212529] hover:bg-[#e2e6ea]/40',
            'group flex items-center justify-between w-full px-3 py-2.5 text-[14px] font-medium rounded-lg transition-colors duration-200'
          )}>
          <div className="flex items-center min-w-0">
            <Tooltip title={label} placement="right" disableHoverListener={!collapsed}>
              <item.icon
                className={classNames(
                  'flex-shrink-0 h-5 w-5 text-[#5a6268] group-hover:text-[#1c1f23] transition-colors',
                  !collapsed && 'mr-3'
                )}
              />
            </Tooltip>
            {!collapsed && <span className="truncate pr-2">{label}</span>}
          </div>

          {!collapsed && (
            <KeyboardArrowDownIcon
              className={classNames(
                'h-4 w-4 text-[#5a6268] transition-transform duration-200',
                isOpen && 'transform rotate-180'
              )}
            />
          )}
        </button>

        <div
          className={classNames(
            'mt-0.5 space-y-0.5 transition-all duration-200 overflow-hidden',
            isOpen && !collapsed ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          )}>
          {filteredChildren.map((child: any) => {
            const childLabel = child?.translate === false ? child.name : t(`menu.${child.name}`);
            return (
              <NavLink
                key={child.name}
                to={child.href}
                end={child.end}
                className={({ isActive }) =>
                  classNames(
                    isActive
                      ? 'bg-[#e2e6ea] text-[#1c1f23] font-semibold'
                      : 'text-[#212529]/90 hover:bg-[#e2e6ea]/40',
                    'group flex items-center py-2 text-[13.5px] font-medium rounded-lg transition-colors',
                    collapsed ? 'px-2 justify-center' : 'pl-11 pr-3'
                  )
                }>
                {child.icon && (
                  <Tooltip title={childLabel} placement="right" disableHoverListener={!collapsed}>
                    <child.icon
                      className={classNames(
                        'flex-shrink-0 h-4 w-4 text-[#5a6268]',
                        !collapsed && 'mr-3'
                      )}
                    />
                  </Tooltip>
                )}
                {!collapsed && <span className="truncate">{childLabel}</span>}
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      end={item.end}
      className={({ isActive }) =>
        classNames(
          isActive
            ? 'bg-[#e2e6ea] text-[#1c1f23] font-semibold'
            : 'text-[#212529] hover:bg-[#e2e6ea]/40',
          'group flex items-center px-3 py-2.5 text-[14px] font-medium rounded-lg transition-colors'
        )
      }>
      <div className="flex items-center min-w-0">
        <Tooltip title={label} placement="right" disableHoverListener={!collapsed}>
          <item.icon
            className={classNames(
              'flex-shrink-0 h-5 w-5 text-[#5a6268] group-hover:text-[#1c1f23] transition-colors',
              !collapsed && 'mr-3'
            )}
          />
        </Tooltip>
        {!collapsed && <span className="mx-2 truncate">{label}</span>}
      </div>
    </NavLink>
  );
};

export const Menu = (navigation: any[], collapsed?: boolean) => {
  const { t } = useTranslation();
  const groups = useAppSelector((state) => state.auth.groups);
  const context = useAppSelector((state) => state.contextData.context);

  return (
    <nav id="menu" className="w-full px-2 space-y-0.5">
      {navigation
        // I filtri ora si attivano correttamente ad ogni aggiornamento di Redux
        .filter(
          (item) =>
            !item.roles ||
            item.roles?.length === 0 ||
            item.roles?.some((role: string) => groups.includes(role))
        )
        .filter((item) => !item.contexts || item.contexts.includes(context))
        .map((item, index) =>
          item.spacer ? (
            <div className="h-4" key={`spacer-${index}`}></div>
          ) : (
            <div key={item.name}>
              <MenuItem item={item} collapsed={collapsed} t={t} groups={groups} context={context} />
            </div>
          )
        )}
    </nav>
  );
};

export default Menu;

// import { classNames } from 'helpers/classNames';
// import { useTranslation } from 'react-i18next';
// import { NavLink } from 'react-router-dom';
// import { useAppSelector } from 'app/store';
// import { Tooltip } from '@mui/material';

// export const Menu = (navigation: any[], collapsed?: boolean) => {
//   const { t } = useTranslation();
//   const groups = useAppSelector((state) => state.auth.groups);
//   const context = useAppSelector((state) => state.contextData.context);

//   return (
//     <nav id="menu" className="w-full px-2 pt-2 space-y-1">
//       {navigation
//         .filter(
//           (item) => !item.roles || item.roles?.length === 0 || item.roles?.includes(groups[0])
//         )
//         .filter((item) => !item.contexts || item.contexts.includes(context))
//         .map((item, index) =>
//           item.spacer ? (
//             <div className="h-4" key={`spacer-${index}`}></div>
//           ) : (
//             <div key={item.name}>
//               <NavLink
//                 to={item.href}
//                 end={item.end}
//                 className={({ isActive }) =>
//                   classNames(
//                     isActive
//                       ? 'bg-secondary-800 text-white'
//                       : 'text-neutral-100 hover:bg-secondary-800 hover:text-neutral-100',
//                     'group flex items-center px-2 py-2 text-base font-medium rounded-md'
//                   )
//                 }>
//                 <div className="flex">
//                   <Tooltip
//                     id="boh"
//                     title={item?.translate === false ? item.name : t(`menu.${item.name}`)}
//                     placement="right">
//                     <item.icon
//                       className={classNames(
//                         'flex-shrink-0 h-6 w-6 text-neutral-100',
//                         !collapsed && 'mr-4'
//                       )}
//                     />
//                   </Tooltip>
//                   {!collapsed && (
//                     <span className="mx-2">
//                       {item?.translate === false ? item.name : t(`menu.${item.name}`)}
//                     </span>
//                   )}
//                 </div>
//               </NavLink>
//             </div>
//           )
//         )}
//     </nav>
//   );
// };

// export default Menu;
